import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
  useCallback,
} from 'react'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {getUserByToken} from './_requests'
import {WithChildren} from '../../../../_metronic/helpers'
import {useTokenExpirationMiddleware} from './TokenExpirationMiddleware'
import SessionExpiredModal from '../components/SessionExpiredModal'
import {useProviderInfo} from './useProviderInfo'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: () => void
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false)

  const saveAuth = (auth: AuthModel | undefined) => {
    // console.log('Saving auth:', auth) // Debug log
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const saveUser = (user: UserModel | undefined) => {
    console.log('Saving user:', user) // Debug log
    setCurrentUser(user)
    if (user) {
      // If we have a user, make sure their token matches the auth token
      if (auth?.token) {
        user.token = auth.token
      }
    }
  }

  const logout = useCallback(() => {
    console.log('Logout called, cleaning up auth state')
    saveAuth(undefined)
    setCurrentUser(undefined)
    authHelper.removeUser()
    setShowSessionExpiredModal(false) // Hide modal if logout is called directly

    // Only redirect if not already on auth pages to prevent infinite loops
    const currentPath = window.location.pathname
    if (!currentPath.includes('/auth/')) {
      console.log('Redirecting to login page')
      window.location.href = '/auth/login'
    }
  }, [])

  // Handle token expiration - show modal instead of immediate logout
  const handleTokenExpiration = useCallback(() => {
    console.log('Token expired, showing session expired modal')
    setShowSessionExpiredModal(true)
  }, [])

  // Handle login redirect from modal
  const handleLoginRedirect = useCallback(() => {
    console.log('User confirmed login redirect from modal')
    const middleware = middlewareRef.current
    if (middleware) {
      middleware.performCleanupAndRedirect()
    } else {
      // Fallback if middleware is not available
      authHelper.removeAuth()
      authHelper.removeUser()
      window.location.href = '/auth/login'
    }
    setShowSessionExpiredModal(false)
  }, [])

  // Initialize token expiration monitoring with modal callback
  const middlewareRef = useRef<any>(null)
  middlewareRef.current = useTokenExpirationMiddleware(auth, handleTokenExpiration)

  return (
    <AuthContext.Provider value={{auth, saveAuth, currentUser, setCurrentUser, logout}}>
      {children}
      <SessionExpiredModal show={showSessionExpiredModal} onLoginRedirect={handleLoginRedirect} />
    </AuthContext.Provider>
  )
}

// Add JWT decode function
const decodeJWT = (token: string) => {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch (error) {
    console.error('Error decoding JWT:', error)
    return null
  }
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {auth, logout, saveAuth, setCurrentUser, currentUser} = useAuth()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  const [providerDataReady, setProviderDataReady] = useState(false)

  // React Query: Fetch provider info with automatic caching and background refresh
  const {data: providerInfo, isLoading: isLoadingProvider} = useProviderInfo()

  // Update user with fresh provider info from API (background refresh)
  // NO localStorage - only React Query cache + in-memory state
  useEffect(() => {
    if (providerInfo && currentUser && !isLoadingProvider && auth) {
      // Only update if provider data has actually changed (prevent infinite loop)
      const hasProviderChanged =
        !auth.provider ||
        auth.provider.providerCode !== providerInfo.providerCode ||
        auth.provider.providerName !== providerInfo.providerName

      if (!hasProviderChanged && providerDataReady) {
        return // Already up to date
      }

      console.log('Provider data loaded from API:', providerInfo)

      // Merge fresh provider data with current user
      const updatedUser: UserModel = {
        ...currentUser,
        providerCode: providerInfo.providerCode,
        providerName: providerInfo.providerName,
        providerAddress: providerInfo.address,
        providerCity: providerInfo.city,
        providerProvince: providerInfo.province,
        providerCountry: providerInfo.country,
        roleId: providerInfo.roleId,
        fullname: providerInfo.providerName,
        email: providerInfo.email,
        isActive: providerInfo.isActive,
      }      
      // Update user state (in-memory only)
      setCurrentUser(updatedUser)

      // ALSO update auth object with provider data (in-memory)
      // This ensures components using auth.provider.providerCode get the data      
      const updatedAuth: AuthModel = {
        ...auth,
        provider: {
          providerCode: providerInfo.providerCode,
          providerName: providerInfo.providerName,
          providerAddress: providerInfo.address,
          providerCity: providerInfo.city,
          providerProvince: providerInfo.province,
          providerCountry: providerInfo.country,
          isActive: providerInfo.isActive,
          phone: providerInfo.phone,
          roleId: providerInfo.roleId,
        },
      }
      saveAuth(updatedAuth)      

      // Mark provider data as ready
      setProviderDataReady(true)
    }
  }, [providerInfo, isLoadingProvider, currentUser, auth, providerDataReady]) // Include all dependencies

  useEffect(() => {
    const requestUser = async (token: string, authData?: any) => {
      try {
        if (!didRequest.current) {
          if (authData?.isImpersonation) {
            // Decode the token to get email
            const decodedToken = decodeJWT(token)
            const userEmail =
              decodedToken?.sub ||
              decodedToken?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name']

            // For impersonation, create user from auth data
            // Map the API menu structure to the provider portal's expected structure
            const defaultMenuAccess = {view: false, export: false, edit: false, create: false}
            const menuAccess = {
              // Map claim directly (same name)
              claim: authData.menu?.claim || {...defaultMenuAccess},
              // Map provider directly (same name)
              provider: authData.menu?.provider || {...defaultMenuAccess},
              // Map other menu items to their expected counterparts
              icd: {view: true, export: true, edit: true, create: true}, // Default to true for ICD
              // Map account to accountprovider
              accountprovider: authData.menu?.account || {...defaultMenuAccess},
              // Default for activationprovider
              activationprovider: {view: true, export: true, edit: true, create: true},
            }

            const impersonatedUser: UserModel = {
              id: 1, // Default ID for impersonated user
              username: authData.provider.providerCode,
              password: '',
              email: userEmail || authData.provider.providerCode, // Use email from token, fallback to provider code
              first_name: authData.provider.providerName,
              last_name: '',
              fullname: authData.provider.providerName,
              roles: [],
              token: token,
              providerCode: authData.provider.providerCode,
              providerName: authData.provider.providerName,
              providerAddress: authData.provider.providerAddress,
              providerCity: authData.provider.providerCity,
              providerProvince: authData.provider.providerProvince,
              providerCountry: authData.provider.providerCountry,
              isActive: authData.provider.isActive || false,
              menuAccess: menuAccess,
            }
            setCurrentUser(impersonatedUser)
            // For impersonation, provider data is already available, so mark as ready
            setProviderDataReady(true)
          } else {
            // Normal flow - get user from token
            // Provider data will be fetched by React Query in background
            const userData = await getUserByToken(token)
            if (userData) {
              setCurrentUser(userData)
            }
          }
        }
      } catch (error) {
        console.error('Error setting up user:', error)
        if (!didRequest.current) {
          logout()
        }
      } finally {
        setShowSplashScreen(false)
      }
    }

    // Check URL parameters
    const params = new URLSearchParams(window.location.search)
    const impersonationToken = params.get('token')
    const encodedAuthData = params.get('authData')    

    if (impersonationToken && encodedAuthData) {
      try {
        // Decode and parse the auth data
        const authData = JSON.parse(atob(encodedAuthData))

        // Map the API menu structure to the provider portal's expected structure for auth
        const defaultMenuAccess = {view: false, export: false, edit: false, create: false}
        const mappedMenu = {
          claim: authData.menu?.claim || {...defaultMenuAccess},
          provider: authData.menu?.provider || {...defaultMenuAccess},
          icd: {view: true, export: true, edit: true, create: true}, // Default to true for ICD
          accountprovider: authData.menu?.account || {...defaultMenuAccess},
          activationprovider: {view: true, export: true, edit: true, create: true},
        }

        // Save the auth state with mapped menu
        saveAuth({
          token: impersonationToken,
          provider: authData.provider,
          menu: mappedMenu,
        })

        // Initialize the user
        requestUser(impersonationToken, authData)
      } catch (error) {
        console.log('Error processing impersonation data:', error)
        console.error('Detailed error:', error)
        logout()
        setShowSplashScreen(false)
      }
    } else if (auth && auth.token) {
      // Normal login flow
      requestUser(auth.token)
    } else {
      logout()
      setShowSplashScreen(false)
    }

    return () => {
      didRequest.current = true
    }
  }, [])

  // Wait for provider data to be ready before rendering app
  // This prevents components from making API calls with undefined providerCode
  const shouldShowSplashScreen = showSplashScreen || (auth && !providerDataReady)

  return shouldShowSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
